import React, { useEffect, useMemo, useRef, useState } from 'react';
import PageLayout from './layout/PageLayout';
import NovelCard from './NovelCard';
import LibraryFiltersPanel from './LibraryFiltersPanel.jsx';
import Pagination from './Pagination/Pagination.jsx';
import BrowseToolbar from './BrowseToolbar';
import ActiveFilterChips from './ActiveFilterChips';
import useLibraryData from '../hooks/useLibraryData';
import { useNovelFilters } from '../hooks/useNovelFilters';
import Button from './common/ui/Button';
import Seo from './common/Seo';
import { BRAND_NAME } from '../constants/brand';
import './NovelList.css';

const LIBRARY_FILTERS_STORAGE_KEY = 'novelday.libraryFilters';

function NovelList() {
  const [pageSize] = useState(12);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const filterButtonRef = useRef(null);
  const pageContainerRef = useRef(null);

  const {
    filters,
    dispatchFilters,
    draftFilters,
    dispatchDraftFilters,
    currentPage,
    setCurrentPage,
    pendingScrollToTop,
    setPendingScrollToTop,
    activeFilterCount,
    hasActiveFilters,
    initialFilterState,
    filtersHydrated,
  } = useNovelFilters({ storageKey: LIBRARY_FILTERS_STORAGE_KEY });

  const queryParams = useMemo(() => {
    const params = {
      page: currentPage,
      size: pageSize,
      sort: filters.sort || initialFilterState.sort,
      genreInclusionMode: filters.genreInclusionMode,
      tagInclusionMode: filters.tagInclusionMode,
    };

    // Browse page does not support text search (use /search for that)

    const mapToNumericArray = (values) =>
      (values ?? [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

    const includeGenreIds = mapToNumericArray(filters.includeGenreIds);
    if (includeGenreIds.length > 0) {
      params.includeGenreIds = includeGenreIds;
    }

    const excludeGenreIds = mapToNumericArray(filters.excludeGenreIds);
    if (excludeGenreIds.length > 0) {
      params.excludeGenreIds = excludeGenreIds;
    }

    const includeTagIds = mapToNumericArray(filters.includeTagIds);
    if (includeTagIds.length > 0) {
      params.includeTagIds = includeTagIds;
    }

    const excludeTagIds = mapToNumericArray(filters.excludeTagIds);
    if (excludeTagIds.length > 0) {
      params.excludeTagIds = excludeTagIds;
    }

    const assignNumeric = (key, value) => {
      if (value === '' || value === null || value === undefined) {
        return;
      }
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) {
        params[key] = parsed;
      }
    };

    assignNumeric('minRating', filters.minRating);
    assignNumeric('maxRating', filters.maxRating);
    assignNumeric('minRatingCount', filters.minRatingCount);
    assignNumeric('maxRatingCount', filters.maxRatingCount);
    assignNumeric('minChapterCount', filters.minChapterCount);
    assignNumeric('maxChapterCount', filters.maxChapterCount);
    assignNumeric('minViewCount', filters.minViewCount);
    assignNumeric('maxViewCount', filters.maxViewCount);

    if (filters.isCompleted === 'completed') {
      params.isCompleted = true;
    } else if (filters.isCompleted === 'ongoing') {
      params.isCompleted = false;
    }

    if (filters.originCountry && filters.originCountry !== '') {
      params.originCountry = filters.originCountry;
    }

    if (filters.hideMature) {
      params.hideMature = true;
    }

    const excludeContentWarningIds = mapToNumericArray(filters.excludeContentWarningIds);
    if (excludeContentWarningIds.length > 0) {
      params.excludeContentWarningIds = excludeContentWarningIds;
    }

    return params;
  }, [currentPage, pageSize, filters, initialFilterState.sort]);

  const { data, loading, error, execute, hasFetched, isIdle } = useLibraryData({
    params: queryParams,
    immediate: false,
  });

  // Hydration is handled by useNovelFilters hook

  useEffect(() => {
    if (filtersHydrated) {
      execute();
    }
  }, [execute, filtersHydrated]);

  const pageData = data?.novelsPage;
  const genres = useMemo(() => {
    const raw = data?.genres ?? [];
    const PRIORITY = ['Action', 'Adventure', 'Fantasy', 'Romance', 'Mystery', 'Comedy', 'Drama', 'Sci-fi', 'Horror', 'Thriller', 'Isekai', 'Slice of Life', 'Supernatural', 'Magic', 'Martial Arts', 'Wuxia', 'Xianxia', 'Xuanhuan', 'Game', 'System', 'Reincarnation', 'School Life', 'Urban', 'Historical', 'Psychological', 'Mecha', 'Sports', 'Music', 'LitRPG'];
    const priorityIndex = (name) => PRIORITY.findIndex(p => name.toLowerCase() === p.toLowerCase());
    return [...raw].sort((a, b) => {
      const ia = priorityIndex(a.name);
      const ib = priorityIndex(b.name);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [data]);
  const tags = useMemo(() => data?.tags ?? [], [data]);
  const contentWarnings = useMemo(() => data?.contentWarnings ?? [], [data]);
  const novels = pageData?.content ?? [];
  // Handle both old format (totalPages at root) and new VIA_DTO format (totalPages in page object)
  const totalPages = pageData?.totalPages ?? pageData?.page?.totalPages ?? 0;

  const genreLookup = useMemo(() => {
    const map = new Map();
    genres.forEach((g) => { if (g?.id != null) map.set(String(g.id), g); });
    return map;
  }, [genres]);

  const tagLookup = useMemo(() => {
    const map = new Map();
    tags.forEach((t) => { if (t?.id != null) map.set(String(t.id), t); });
    return map;
  }, [tags]);

  const warningLookup = useMemo(() => {
    const map = new Map();
    contentWarnings.forEach((w) => { if (w?.id != null) map.set(String(w.id), w); });
    return map;
  }, [contentWarnings]);

  useEffect(() => {
    if (isFilterPanelOpen) {
      dispatchDraftFilters({ type: 'REPLACE', value: filters });
    }
  }, [isFilterPanelOpen, filters, dispatchDraftFilters]);

  useEffect(() => {
    if (!pendingScrollToTop || loading) {
      return;
    }

    const target = pageContainerRef.current;
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    setPendingScrollToTop(false);
  }, [pendingScrollToTop, loading, setPendingScrollToTop]);

  const handlePageChange = (nextPage) => {
    if (nextPage >= 0 && nextPage < totalPages) {
      setCurrentPage(nextPage);
      setPendingScrollToTop(true);
    }
  };

  const handleGenreToggle = (genreId) => {
    const id = String(genreId);
    const currentInclude = new Set((filters.includeGenreIds || []).map(String));
    const currentExclude = new Set((filters.excludeGenreIds || []).map(String));

    // Toggle logic: If included, remove. If not, add.
    // Also ensure it's removed from excluded if present.
    if (currentInclude.has(id)) {
      currentInclude.delete(id);
    } else {
      currentInclude.add(id);
      if (currentExclude.has(id)) {
        currentExclude.delete(id);
      }
    }

    const nextFilters = {
      includeGenreIds: Array.from(currentInclude),
      excludeGenreIds: Array.from(currentExclude),
    };

    dispatchFilters({ type: 'REPLACE', value: { ...filters, ...nextFilters } });
    dispatchDraftFilters({ type: 'REPLACE', value: { ...filters, ...nextFilters } });
    setCurrentPage(0);
    setPendingScrollToTop(true);
  };

  const handleQuickFilterChange = (field, value) => {
    const nextFilters = { [field]: value };
    dispatchFilters({ type: 'REPLACE', value: { ...filters, ...nextFilters } });
    dispatchDraftFilters({ type: 'REPLACE', value: { ...filters, ...nextFilters } });
    setCurrentPage(0);
    setPendingScrollToTop(true);
  };

  const handleDraftChange = (field, value) => {
    dispatchDraftFilters({ type: 'SET_FIELD', field, value });
  };

  const handleApplyFilters = () => {
    dispatchFilters({ type: 'REPLACE', value: draftFilters });
    setCurrentPage(0);
    setPendingScrollToTop(true);
    setIsFilterPanelOpen(false);
    filterButtonRef.current?.focus();
  };

  const handleResetDraftFilters = () => {
    dispatchDraftFilters({ type: 'RESET' });
  };

  const handleClearFilters = () => {
    dispatchFilters({ type: 'RESET' });
    dispatchDraftFilters({ type: 'RESET' });
    setCurrentPage(0);
    setPendingScrollToTop(true);
  };

  const handleClosePanel = () => {
    setIsFilterPanelOpen(false);
    filterButtonRef.current?.focus();
  };

  const removeFromFilterArray = (field, value) => {
    const next = (filters[field] ?? []).filter((item) => String(item) !== String(value));
    dispatchFilters({ type: 'SET_FIELD', field, value: next });
    dispatchDraftFilters({ type: 'SET_FIELD', field, value: next });
    setCurrentPage(0);
    setPendingScrollToTop(true);
  };

  // Skeleton shows while: filters hydrate from localStorage, the first fetch
  // is in flight, OR the fetch is idle (hasn't started yet — the gap between
  // hydration and the execute() effect). Without the isIdle check, the page
  // briefly renders "No novels matched" (a short page) between the hydrated
  // and loading states, causing a massive layout shift (CLS) when the tall
  // skeleton/grid reappears.
  if ((!filtersHydrated) || (loading && !hasFetched) || isIdle) {
    return (
      <PageLayout noMobilePadding>
        <Seo
          title="Browse Novels"
          description={`Browse and filter web novels by genre, tags, rating, status, and more. Find your next favorite story on ${BRAND_NAME}.`}
          url="/library"
          jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Browse Novels',
            url: 'https://novelday.net/library',
            description: `Browse and filter web novels by genre, tags, rating, status, and more on ${BRAND_NAME}.`,
          }}
        />
        <div className="library-page">
          <header className="library-page__header page-header">
            <div className="library-page__header-content">
              <h1 className="type-heading-xl">Browse Novels</h1>
            </div>
            {/* Genre chip skeletons — same height as the real chips row so
                the swap when genres arrive doesn't shift the layout. */}
            <div className="library-chips" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <span key={i} className="library-chip-skeleton" />
              ))}
            </div>
          </header>
          <section className="library-page__content">
            {/* Real toolbar — rendering it during load keeps the height
                identical between skeleton and content states (zero CLS). */}
            <BrowseToolbar
              filters={filters}
              onQuickFilterChange={handleQuickFilterChange}
              onOpenFilterPanel={() => setIsFilterPanelOpen(true)}
              onClearFilters={handleClearFilters}
              activeFilterCount={activeFilterCount}
              hasActiveFilters={hasActiveFilters}
              loading={loading}
            />
            <div className="library-page__body">
              <span className="visually-hidden" aria-live="polite">
                Loading novels…
              </span>
              {/* Skeleton grid — reuses the real .novel-grid class so the
                  column count matches at every breakpoint. 12 cards matches
                  the real page size (pageSize=12). */}
              <div className="novel-grid" aria-hidden="true">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="novel-card-skeleton">
                    <div className="novel-card-skeleton__cover" />
                    <div className="novel-card-skeleton__line novel-card-skeleton__line--title" />
                    <div className="novel-card-skeleton__line novel-card-skeleton__line--author" />
                    <div className="novel-card-skeleton__genres">
                      <div className="novel-card-skeleton__chip" />
                      <div className="novel-card-skeleton__chip" />
                    </div>
                    <div className="novel-card-skeleton__divider" />
                    <div className="novel-card-skeleton__stats">
                      <div className="novel-card-skeleton__stat" />
                      <div className="novel-card-skeleton__stat" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <div className="home-page__state home-page__state--error">
          <p>Failed to load data. Please try again later.</p>
          <Button variant="primary" onClick={execute}>
            Refresh
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout noMobilePadding>
      <Seo
        title="Browse Novels"
        description={`Browse and filter web novels by genre, tags, rating, status, and more. Find your next favorite story on ${BRAND_NAME}.`}
        url="/library"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Browse Novels',
          url: 'https://novelday.net/library',
          description: `Browse and filter web novels by genre, tags, rating, status, and more on ${BRAND_NAME}.`,
        }}
      />
      <div ref={pageContainerRef} className="library-page">
        <header className="library-page__header page-header">
          <div className="library-page__header-content">
            <h1 className="type-heading-xl">Browse Novels</h1>
          </div>
          {/* Quick Genre Chips */}
          <div className="library-chips">
            {genres.map((genre) => {
              const isActive = (filters.includeGenreIds || []).map(String).includes(String(genre.id));
              return (
                <Button
                  key={genre.id}
                  variant={isActive ? 'primary' : 'secondary'}
                  size="sm"
                  className="library-chip-btn"
                  onClick={() => handleGenreToggle(genre.id)}
                >
                  {genre.name}
                </Button>
              );
            })}
          </div>
        </header>
        <section className="library-page__content">
          <BrowseToolbar
            filters={filters}
            onQuickFilterChange={handleQuickFilterChange}
            onOpenFilterPanel={() => setIsFilterPanelOpen(true)}
            onClearFilters={handleClearFilters}
            activeFilterCount={activeFilterCount}
            hasActiveFilters={hasActiveFilters}
            loading={loading}
          />

          {hasActiveFilters && (
            <ActiveFilterChips
              filters={filters}
              initialSort={initialFilterState.sort}
              genreLookup={genreLookup}
              tagLookup={tagLookup}
              warningLookup={warningLookup}
              onRemoveFilter={(field, value) => removeFromFilterArray(field, value)}
              onResetField={(field, value) => {
                dispatchFilters({ type: 'SET_FIELD', field, value });
                dispatchDraftFilters({ type: 'SET_FIELD', field, value });
                setCurrentPage(0);
                setPendingScrollToTop(true);
              }}
            />
          )}
          
          <div className="library-page__body">
            {novels.length === 0 ? (
              <div className="no-results">
                <p>No novels matched your current filters. Try another search or explore a different genre.</p>
              </div>
            ) : (
              <>
                <div className="novel-grid">
                  {novels.map((novel, index) => {
                    const statusLabel = novel.isCompleted ? 'Completed' : 'Ongoing';

                    return (
                      <NovelCard
                        key={novel.id}
                        novel={novel}
                        to={`/novels/${novel.id}`}
                        status={statusLabel}
                        genres={novel.genres}
                        loading={index < 4 ? 'eager' : 'lazy'}
                      />
                    );
                  })}
                </div>

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  isLoading={loading}
                />
              </>
            )}
          </div>
        </section>
      </div>
      <LibraryFiltersPanel
        isOpen={isFilterPanelOpen}
        onClose={handleClosePanel}
        onApply={handleApplyFilters}
        onReset={handleResetDraftFilters}
        filters={draftFilters}
        onChange={handleDraftChange}
        genres={genres}
        tags={tags}
        contentWarnings={contentWarnings}
      />
    </PageLayout>
  );
}

export default NovelList;
